import React from "react";
import { useTheme } from "@emotion/react";
import { AppTheme } from "myplan/utils/theme";
import { useOpenUrl, UrlType } from "myplan/utils/url";
import {
    PhoneIcon,
    SmsIcon,
    WebsiteIcon,
    NavigationIcon,
    HoursIcon,
    TTYIcon,
} from "myplan/components/Icon";
import { Container, Aside, Main, Label } from "./styles";
import { TouchableOpacity } from "react-native";

export enum ResourceFieldType {
    Location = "LOCATION",
    Phone = "PHONE",
    Sms = "SMS",
    Hours = "HOURS",
    Website = "WEBSITE",
    TTY = "TTY",
}

type ResourceFieldProps = {
    type: ResourceFieldType;
    title?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    hours?: string;
    phoneNumber?: string;
    ttyNumber?: string;
    ttyInstructions?: string;
    smsNumber?: string;
    smsLabel?: string;
    smsPrefill?: string;
    websiteUrl?: string;
    websiteUrlLabel?: string;
};

const ResourceField = ({
    type,
    title,
    streetAddress,
    city,
    state,
    zipCode,
    hours,
    phoneNumber,
    ttyNumber,
    ttyInstructions,
    smsNumber,
    smsLabel,
    smsPrefill,
    websiteUrl,
    websiteUrlLabel,
}: ResourceFieldProps) => {
    const {
        color: { accent },
    }: AppTheme = useTheme() as AppTheme;
    const openUrl = useOpenUrl();
    let IconComponent;
    let displayText;
    const onPress = () => {
        switch (type) {
            case ResourceFieldType.Website:
                openUrl(UrlType.Website, { websiteUrl });
                break;

            case ResourceFieldType.Phone:
                openUrl(UrlType.Phone, { phoneNumber });
                break;

            case ResourceFieldType.Sms:
                openUrl(UrlType.Sms, { smsNumber, smsPrefill });
                break;

            case ResourceFieldType.Location:
                openUrl(UrlType.Address, {
                    title,
                    streetAddress,
                    city,
                    state,
                    zipCode,
                });
                break;
        }
    };

    switch (type) {
        case ResourceFieldType.Website:
            IconComponent = WebsiteIcon;
            displayText = (
                <Label type="h9" isLink>
                    {websiteUrlLabel || websiteUrl}
                </Label>
            );
            break;

        case ResourceFieldType.Location:
            IconComponent = NavigationIcon;
            displayText = (
                <>
                    <Label type="h9" isLink>{`${streetAddress}`}</Label>
                    <Label
                        type="h9"
                        isLink>{`${city}, ${state} ${zipCode}`}</Label>
                </>
            );
            break;

        case ResourceFieldType.Phone:
            IconComponent = PhoneIcon;
            displayText = (
                <Label type="h9" isLink>
                    {phoneNumber}
                </Label>
            );
            break;

        case ResourceFieldType.TTY:
            IconComponent = TTYIcon;
            displayText = (
                <Label type="h9" isLink={!!ttyNumber}>
                    {ttyNumber || ttyInstructions}
                </Label>
            );
            break;

        case ResourceFieldType.Sms:
            IconComponent = SmsIcon;
            displayText = (
                <Label type="h9" isLink>
                    {smsLabel || smsNumber}
                </Label>
            );
            break;

        case ResourceFieldType.Hours:
            IconComponent = HoursIcon;
            displayText = <Label type="h9">{hours}</Label>;
            break;
    }

    return (
        <TouchableOpacity onPress={onPress}>
            <Container>
                <Aside>
                    {IconComponent && (
                        <IconComponent width="30" height="30" color={accent} />
                    )}
                </Aside>
                <Main>{displayText}</Main>
            </Container>
        </TouchableOpacity>
    );
};

export default ResourceField;
